<template>
  <input
    type="text"
    :id="searchId"
    :name="browserAutocompleteName"
    class="autocomplete"
    :autocomplete="browserAutocompleteValue"
    v-model="searchItem"
    @focus="handleClear"
  />
  <label :for="searchId" v-if="fieldName && !placeHolder">{{ fieldName }}</label>
  <label class="active" :for="searchId" v-if="fieldName && placeHolder">{{ fieldName }}</label>

  <div class="progress" v-if="isSearching">
  <div class="indeterminate"></div>
  </div>

</template>
    
        
<script>
import { ref, onMounted, onBeforeUnmount, watch, watchEffect, computed } from "vue";
import M from "materialize-css";

export default {
  emits: ["selected", "searching", "clear"],
  props: [
    "fieldName",
    "inputId",
    "search",
    "data",
    "clear",
    "placeHolder",
    "browserAutocomplete",
    "appendDropdownToBody",
    "forceDropdownDown",
    "dropdownClass",
  ],
  setup(props, context) {
    const selected = ref(false);
    const isSearching = ref(false);
    const searchItem = ref(null);
    const searchId = ref(props.inputId);
    const browserAutocompleteValue = computed(() => props.browserAutocomplete || "off");
    const browserAutocompleteName = computed(() => `${searchId.value || "materialize"}-lookup`);


    // clear field
    const handleClear = () => {
      searchItem.value = null;
      // end progress bar
      isSearching.value = false;
      // clear select
      selected.value = false;
      // emit selected (cleared)
      context.emit("selected", { id: searchId.value, value: null });
    };

    // special case 
    // where there is a default data value
    // if you don't want to search the default value
    // turn off the search bolean
    // till the clear button sends null value back to parent user
    watchEffect(() => {
      if (props.placeHolder) {
        searchItem.value = props.placeHolder;
      }
    });

    // searching feedbacks
    watchEffect(() => {
      if (searchItem.value && props.search) {
        context.emit("searching", {
          id: searchId.value,
          value: searchItem.value,
        });
      // start progress bar (if not selected)
      if (!selected.value) {
      isSearching.value = true;
      }  
      }else{
       // end progress bar
      isSearching.value = false;  
      }
    });

    // clear searching
    watchEffect(() => {
     if (props.data) {
      // end progress bar
      isSearching.value = false;
     }    
    });

    watchEffect(() => {
      if (props.clear) {
        searchItem.value = null;
        context.emit("clear", {
          id: searchId.value,
          value: false,
        });
      }
    });

    const applyDropdownClass = (instance) => {
      if (!props.dropdownClass || !instance?.container) {
        return;
      }

      props.dropdownClass.split(" ").forEach((className) => {
        if (className) {
          instance.container.classList.add(className);
        }
      });
    };

    const appendDropdownToBody = (instance) => {
      if (!props.appendDropdownToBody || !instance?.container) {
        return;
      }

      if (instance.container.parentNode != document.body) {
        document.body.appendChild(instance.container);
      }
    };

    const forceDropdownDown = (instance) => {
      if (!props.forceDropdownDown || !instance?.dropdown || instance.dropdown._easyHmoForceDown) {
        return;
      }

      const dropdown = instance.dropdown;
      const getDropdownPosition = dropdown._getDropdownPosition.bind(dropdown);

      dropdown._getDropdownPosition = function () {
        const positionInfo = getDropdownPosition();
        const triggerBounds = this.el.getBoundingClientRect();
        const dropdownBounds = this.dropdownEl.getBoundingClientRect();
        const availableHeight = window.innerHeight - triggerBounds.bottom - 16;

        return {
          ...positionInfo,
          y: triggerBounds.top - dropdownBounds.top + triggerBounds.height,
          verticalAlignment: "top",
          height: Math.min(positionInfo.height, Math.max(120, availableHeight)),
        };
      };

      dropdown._easyHmoForceDown = true;
    };

    const tuneAutocompleteDropdown = (instance) => {
      applyDropdownClass(instance);
      appendDropdownToBody(instance);
      forceDropdownDown(instance);
    };

    // autocomlete function
    const initializeAutocomplete = () => {
      const elem = document.getElementById(searchId.value);
      const instance = M.Autocomplete.init(elem, {
        data: props.data,
        onAutocomplete: function (val) {
          searchItem.value = val;
          context.emit("selected", { id: searchId.value, value: val });
          // set selected
          selected.value = true;
        },
        minLength: 0,
      });

      tuneAutocompleteDropdown(instance);
    };

    // update autocomplete data function
    const updateAutocompleteData = (newVal) => {
      const elem = document.getElementById(searchId.value);
      const instance = M.Autocomplete.getInstance(elem);

      // if empty (fail quietly)
      if (instance) {
        tuneAutocompleteDropdown(instance);
        instance.updateData(newVal);
        if (props.search && !selected.value) {
          instance.open(); // Open the dropdown for new searches only
        } 
      }
      
    };

    // watch for changes
    watch(props, (newVal) => {
      updateAutocompleteData(newVal.data);
    });

    // initiate autocomplete
    onMounted(() => {
      initializeAutocomplete();
    });

    onBeforeUnmount(() => {
      const elem = document.getElementById(searchId.value);
      const instance = elem ? M.Autocomplete.getInstance(elem) : null;

      if (instance) {
        if (instance.dropdown) {
          instance.dropdown.destroy();
        }

        instance.destroy();
      }
    });

    return {
      isSearching,
      handleClear,
      searchItem,
      searchId,
      browserAutocompleteValue,
      browserAutocompleteName,
    };
  },
};
</script>
<style scoped>
.progress {
  margin-top: -10px; /* Adjust the value as needed to move the progress bar upwards */
}
</style>
        
